// 9fbef606107a605d69c0edbcd8029e5d
import { attributeListMaker } from '@tcp/core/src/services/abstractors/productListing/productParser.js';
import { isClient } from '../../../utils/utils';

const logger = require('@tcp/core/src/utils/loggerInstance');

function altImageArray(imagename, altImg) {
  try {
    const altImges = JSON.parse(altImg);
    return (altImges[imagename] && altImges[imagename].split(',').filter((img) => img)) || [];
  } catch (error) {
    return [];
  }
}

// inner function
function parseAltImagesForColor(imageBasePath, hasShortImage, altImgs, getImgPath, productImage) {
  try {
    const altImages = altImgs ? altImageArray(imageBasePath, altImgs) : [];
    const shortImage = [];
    const normalImage = [];
    let availableImages;
    let imageExtension = '';

    if (productImage) {
      const productImageExt = productImage.split('.');
      imageExtension = productImageExt[productImageExt.length - 1];
    }

    altImages.forEach((image) => {
      if (image?.toLowerCase()?.includes('-s') && !image?.toLowerCase()?.includes('-sizechart')) {
        shortImage.push(image);
      } else {
        normalImage.push(image);
      }
    });

    if (hasShortImage && shortImage.length) {
      availableImages = [...shortImage, ...normalImage];
    } else {
      availableImages = [imageBasePath, ...normalImage];
    }

    return availableImages.map((img) => {
      const hasExtension =
        img.indexOf('.jpg') !== -1 || img.indexOf('.mp4') !== -1 || img.indexOf('.png') !== -1; // we currently only support .jpg but we can make this a regex in the future if needed
      const { productImages } = getImgPath(img, hasExtension, imageExtension);

      // See DTN-155 for image suffex value definitions
      const isOnModalImage =
        parseInt(img.split('-')[1], 10) > 5 || img.split('-')[1] === 'sizechart'; // this is assumming a structure of <alpahnumeric>-<numeric><other (optional)>
      const isShortImage =
        img?.toLowerCase()?.includes('-s') && !img?.toLowerCase()?.includes('-sizechart');

      return {
        isShortImage,
        isOnModalImage,
        iconSizeImageUrl: productImages[125],
        listingSizeImageUrl: productImages[380],
        regularSizeImageUrl: productImages[500],
        bigSizeImageUrl: productImages[900],
        superSizeImageUrl: productImages[900],
      };
    });
  } catch (error) {
    return [];
  }
}

export function isDuplicateColorsPresent(colors) {
  if (!colors || colors?.length <= 1) {
    return false;
  }
  if (Array.isArray(colors)) {
    const secondValues = colors?.map((item) => item?.split('#')[1]);
    const uniqueValues = [...new Set(secondValues)];
    if (colors?.length !== uniqueValues?.length) {
      return true;
    }
  }
  return false;
}

// We seem to be iterating over all colors and added alt images in this location
// eslint-disable-next-line max-params, sonarjs/cognitive-complexity
export function extractExtraImages(
  rawColors,
  altImgs,
  getImgPath,
  uniqueId,
  defaultColor,
  isGiftCard,
  hasShortImage,
  productImage,
  isFromProductParser
) {
  const colorsImageMap = {};
  // backend send the colors in a very weird format
  let imageExtension = '';
  if (productImage) {
    const productImageExt = productImage.split('.');
    imageExtension = productImageExt[productImageExt.length - 1];
  }

  try {
    if (rawColors && rawColors !== '') {
      // DTN-6314 Gift card pdp page broken
      // handle senario if gift card product_name contains '|' character in it.
      let colors = [];

      if (isGiftCard) {
        colors.push(rawColors);
      } else {
        colors = rawColors.split('|');
      }
      const isDuplicate = isDuplicateColorsPresent(colors);
      // eslint-disable-next-line
      for (let color of colors) {
        let colorName = color.split('#')[1];
        let imageBasePath = color.split('#')[0];
        if (uniqueId && isFromProductParser && isDuplicate) {
          colorName = `${color.split('#')[1]}-${imageBasePath}`;
        }
        if (!colorName) {
          colorName = defaultColor;
          imageBasePath = uniqueId;
        }

        const { productImages } = getImgPath(imageBasePath, '', imageExtension);

        colorsImageMap[colorName] = {
          basicImageUrl: productImages[500],
          extraImages: parseAltImagesForColor(
            imageBasePath,
            hasShortImage,
            altImgs,
            getImgPath,
            productImage
          ),
        };
      }
    } else {
      const { productImages } = getImgPath(uniqueId, '', imageExtension);
      colorsImageMap[defaultColor] = {
        basicImageUrl: productImages[500],
        extraImages: parseAltImagesForColor(
          uniqueId,
          hasShortImage,
          altImgs,
          getImgPath,
          productImage
        ),
      };
    }
  } catch (error) {
    logger.error(error);
  }
  return colorsImageMap;
}

export const getAccessoriesData = (baseProduct) => {
  const result =
    attributeListMaker(baseProduct?.list_of_attributes)?.filter((attribute) => {
      return attribute.identifier === 'accessories';
    }) || [];

  const accessoriesProductID = result?.map((data) => data.value.replace(/ /g, '').split(','));

  const result2 =
    attributeListMaker(baseProduct?.list_of_attributes)?.filter((attribute) => {
      return attribute.identifier === 'listingAllowed';
    }) || [];

  const listingAllowed = result2?.map((data) => data.value);

  const result3 =
    attributeListMaker(baseProduct?.list_of_attributes)?.filter((attribute) => {
      return attribute.identifier === 'customizationType';
    }) || [];

  const customizationType = result3?.map((data) => data.value);

  const result4 = attributeListMaker(baseProduct.list_of_attributes)?.filter((attribute) => {
    return attribute.identifier === 'allowMultipleSelect';
  });
  const allowMultipleSelect = result4?.map((data) => data.value);
  return {
    accessoriesProductID: accessoriesProductID[0],
    listingAllowed: listingAllowed[0],
    customizationType: customizationType[0],
    allowMultipleSelect: allowMultipleSelect && allowMultipleSelect[0],
  };
};

const getBrandFacet = () => {
  return isClient() && window.location.href.includes('/ca/')
    ? 'tcpprimarybrandcanadastore_ufilter'
    : 'tcpprimarybrandusstore_ufilter';
};

export const FACETS_FIELD_KEY = {
  color: 'tcpcolor_ufilter',
  size: 'v_tcpsize_ufilter',
  age: 'age_group_ufilter',
  price: 'unbxd_price_range_ufilter',
  sort: 'sort',
  unbxdDisplayName: 'unbxddisplayname',
  aux_color: 'auxdescription_ufilter',
  aux_color_unbxd: 'auxdescription_uFilter',
  l1category: 'l1category',
  display: 'display_group_uFilter',
  grouped_size: 'v_tcp_groupedsize_uFilter',
  fit: 'v_tcpfit_unbxd_ufilter',
  priceRange: 'min_offer_price',
  brand: getBrandFacet(),
  consolidation_brand: 'unbxd_brand_ufilter',
  clothingsize: 'v_clothingsize_ufilter',
  cleanClothingSize: 'v_clothingsizefacet_uFilter',
  shoessize: 'v_shoessize_ufilter',
  accessoriessize: 'v_accessoriessize_ufilter',
  rating: 'TCPBazaarVoiceRating_inText_uFilter',
  sizeFitUrl: 'v_sizefiturl_uFilter',
};

export const FACETS_OPTIONS = {
  lowPriceProducts: '$10 and under',
};

export const HEADER_LABELS = {
  lbl_gym: 'Gymboree',
  lbl_snj: 'Sugar & Jade',
  lbl_tcp: "The Children's Place",
  lbl_pjp: 'PJ Place',
};
